import type { CSSProperties } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  MapPin,
  Droplets,
  StickyNote,
  Package,
  Wrench,
  Box,
  KeyRound,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { navItems } from '../../config/navigation';
import { useAuthStore } from '../../stores/auth-store';
import type { UserRole } from '../../types/auth';

// ─────────────────────────────────────────────────────────────
// Sidebar — new navigation design (dark panel, active-bar),
// rendered as a FLAT list in the original old-UI order. No section
// grouping. The logged-in user + account menu now live in the
// TopBar (top-right); see UserMenu.
// ─────────────────────────────────────────────────────────────

const iconMap: Record<string, React.ComponentType<{ size?: number; style?: CSSProperties }>> = {
  LayoutDashboard,
  Building2,
  Users,
  MapPin,
  Droplets,
  StickyNote,
  Package,
  Wrench,
  Box,
  KeyRound,
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
}

// CSS variable references — single source of truth for theme values.
const C = {
  bg:           'var(--color-bg-dark)',
  text:         'var(--color-sidebar-text)',
  textActive:   'var(--color-sidebar-text-active)',
  hover:        'var(--color-sidebar-hover)',
  active:       'var(--color-sidebar-active-bg)',
  divider:      'var(--color-sidebar-divider)',
  accentBright: 'var(--color-accent-bright)',
} as const;

// Change Password lives in the user-menu dropdown, not the main nav.
const CHANGE_PASSWORD_PATH = '/dashboard/change-password';

// Geonomic logo — white-inverted for the dark sidebar. Collapsed shows
// a compact gradient diamond mark instead.
function GeonomicsLogo({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        }}
      >
        <img
          src="/favicon-mark.png"
          alt="Geonomic"
          style={{ width: 24, height: 24, display: 'block' }}
        />
      </div>
    );
  }
  return (
    <img
      src="/geonomic-technologies-logo.png"
      alt="Geonomic Technologies"
      style={{
        height: 30,
        width: 'auto',
        display: 'block',
        filter: 'brightness(0) invert(1)', // white tint for dark bg
        flexShrink: 0,
      }}
    />
  );
}

export function Sidebar({ collapsed, mobileOpen, onToggle, onMobileClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role as UserRole | undefined;

  // Flat list, original order — Change Password is handled by the user menu.
  const visible = navItems.filter(
    (item) => (role ? item.roles.includes(role) : false) && item.path !== CHANGE_PASSWORD_PATH,
  );
  const showLabels = !collapsed || mobileOpen;

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={onMobileClose} />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col transition-[width] duration-300 ease-in-out',
          'max-md:w-64',
          collapsed ? 'md:w-16' : 'md:w-64',
          mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
        )}
        style={{
          background: C.bg,
          color: C.text,
          paddingTop: 18,
          paddingBottom: 12,
        }}
      >
        {/* ── Brand header ───────────────────────────────────── */}
        <div
          className="flex items-center"
          style={{
            gap: 12,
            paddingLeft: collapsed ? 12 : 18,
            paddingRight: collapsed ? 12 : 14,
            justifyContent: collapsed ? 'center' : 'space-between',
            marginBottom: 18,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <GeonomicsLogo collapsed={!showLabels} />
          </div>

          {showLabels && (
            <button
              onClick={onToggle}
              className="hidden md:flex"
              style={{
                width: 26,
                height: 26,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 5,
                color: 'rgba(255,255,255,0.45)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.hover;
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255,255,255,0.45)';
              }}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>

        {/* Collapsed expand button */}
        {!showLabels && (
          <button
            onClick={onToggle}
            className="hidden md:flex"
            style={{
              alignSelf: 'center',
              width: 26,
              height: 26,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              color: 'rgba(255,255,255,0.45)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              marginBottom: 12,
            }}
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        )}

        {/* ── Flat nav (original order, no sections) ─────────── */}
        <nav
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: collapsed ? '0 8px' : '0 12px',
          }}
        >
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 4, listStyle: 'none', padding: 0, margin: 0 }}>
            {visible.map((item) => {
              const Icon = item.icon ? iconMap[item.icon] : null;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/dashboard'}
                    onClick={onMobileClose}
                    title={collapsed ? item.name : undefined}
                  >
                    {({ isActive }) => (
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: collapsed ? '11px 0' : '11px 14px',
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          borderRadius: 6,
                          fontSize: 14.5,
                          fontWeight: 500,
                          color: isActive ? C.textActive : C.text,
                          background: isActive ? C.active : 'transparent',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 120ms, color 120ms',
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = C.hover;
                            e.currentTarget.style.color = C.textActive;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = C.text;
                          }
                        }}
                      >
                        {isActive && (
                          <span
                            aria-hidden
                            style={{
                              position: 'absolute',
                              left: collapsed ? -8 : -12,
                              top: 6,
                              bottom: 6,
                              width: 2.5,
                              borderRadius: 2,
                              background: C.accentBright,
                            }}
                          />
                        )}
                        {Icon && (
                          <Icon
                            size={19}
                            style={{ flexShrink: 0, opacity: isActive ? 1 : 0.85 }}
                          />
                        )}
                        {showLabels && (
                          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name}
                          </span>
                        )}
                      </div>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}
